import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { defaultClientDetails, normalizeBackendQuoteDrawings, normalizeQuoteSections } from '../lib/quoteSession';
import { createQuoteSection } from '../lib/quoteSections';
import { formatPersonName } from '../lib/formatPersonName';

const QuotesContext = createContext();

export const useQuotes = () => {
  const context = useContext(QuotesContext);
  if (!context) {
    throw new Error('useQuotes must be used within a QuotesProvider');
  }
  return context;
};

const sortQuotesNewestFirst = (quotes) =>
  [...quotes].sort((a, b) => {
    const aTime = a.savedAt ? new Date(a.savedAt).getTime() : 0;
    const bTime = b.savedAt ? new Date(b.savedAt).getTime() : 0;
    return bTime - aTime;
  });

export const QuotesProvider = ({ children }) => {
  const [includePricingInExport, setIncludePricingInExport] = useState(() => {
    try {
      const stored = localStorage.getItem('includePricingInExport');
      return stored === null ? true : stored === 'true';
    } catch {
      return true;
    }
  });

  const [includeTemplateInExport, setIncludeTemplateInExport] = useState(() => {
    try {
      const stored = localStorage.getItem('includeTemplateInExport');
      return stored === null ? true : stored === 'true';
    } catch {
      return true;
    }
  });

  // Nij Begun export content — always off by default (explicit opt-in)
  const [includeNijBegunInExport, setIncludeNijBegunInExport] = useState(() => {
    try {
      const stored = localStorage.getItem('includeNijBegunInExport');
      return stored === 'true';
    } catch {
      return false;
    }
  });

  // Opdrachtbon (BoN) as last print/PDF page — off by default
  const [includeBonInExport, setIncludeBonInExport] = useState(() => {
    try {
      const stored = localStorage.getItem('includeBonInExport');
      return stored === 'true';
    } catch {
      return false;
    }
  });

  // Standalone total/investment page (without full VMK template) — off by default
  const [includeTotalPageInExport, setIncludeTotalPageInExport] = useState(() => {
    try {
      const stored = localStorage.getItem('includeTotalPageInExport');
      return stored === 'true';
    } catch {
      return false;
    }
  });

  const [savedQuotes, setSavedQuotes] = useState(() => {
    try {
      const stored = localStorage.getItem('savedQuotes');
      const parsed = stored ? JSON.parse(stored) : [];
      return sortQuotesNewestFirst(parsed);
    } catch (error) {
      console.error('Error loading saved quotes:', error);
      return [];
    }
  });

  const [quoteSections, setQuoteSections] = useState(() => {
    try {
      const stored = localStorage.getItem('quoteSections');
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading quote sections:', error);
      return [];
    }
  });

  const [activeBackendQuote, setActiveBackendQuote] = useState(() => {
    try {
      const stored = localStorage.getItem('activeBackendQuote');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  // Save to localStorage whenever savedQuotes changes
  useEffect(() => {
    try {
      localStorage.setItem('savedQuotes', JSON.stringify(savedQuotes));
    } catch (error) {
      console.error('Error saving quotes to localStorage:', error);
    }
  }, [savedQuotes]);

  useEffect(() => {
    try {
      localStorage.setItem('quoteSections', JSON.stringify(quoteSections));
    } catch (error) {
      console.error('Error saving quote sections to localStorage:', error);
    }
  }, [quoteSections]);

  useEffect(() => {
    try {
      localStorage.setItem('includePricingInExport', String(includePricingInExport));
    } catch (error) {
      console.error('Error saving export pricing preference:', error);
    }
  }, [includePricingInExport]);

  useEffect(() => {
    try {
      localStorage.setItem('includeTemplateInExport', String(includeTemplateInExport));
    } catch (error) {
      console.error('Error saving export template preference:', error);
    }
  }, [includeTemplateInExport]);

  useEffect(() => {
    try {
      localStorage.setItem('includeNijBegunInExport', String(includeNijBegunInExport));
    } catch (error) {
      console.error('Error saving Nij Begun export preference:', error);
    }
  }, [includeNijBegunInExport]);

  useEffect(() => {
    try {
      localStorage.setItem('includeBonInExport', String(includeBonInExport));
    } catch (error) {
      console.error('Error saving BoN export preference:', error);
    }
  }, [includeBonInExport]);

  useEffect(() => {
    try {
      localStorage.setItem('includeTotalPageInExport', String(includeTotalPageInExport));
    } catch (error) {
      console.error('Error saving total-page export preference:', error);
    }
  }, [includeTotalPageInExport]);

  // Template pack already includes the investment page — keep total-page flag in sync
  useEffect(() => {
    if (includeTemplateInExport && !includeTotalPageInExport) {
      setIncludeTotalPageInExport(true);
    }
  }, [includeTemplateInExport, includeTotalPageInExport]);

  useEffect(() => {
    try {
      if (activeBackendQuote) {
        localStorage.setItem('activeBackendQuote', JSON.stringify(activeBackendQuote));
      } else {
        localStorage.removeItem('activeBackendQuote');
      }
    } catch (error) {
      console.error('Error saving active backend quote:', error);
    }
  }, [activeBackendQuote]);

  const loadBackendQuoteSession = useCallback((quote) => {
    if (!quote?.quote_data) return;
    const drawings = normalizeBackendQuoteDrawings(quote.quote_data);
    setSavedQuotes(drawings);
    setQuoteSections(normalizeQuoteSections(quote.quote_data));
    const exportOptions = quote.quote_data.exportOptions || {};
    if (typeof exportOptions.includePricingInExport === 'boolean') {
      setIncludePricingInExport(exportOptions.includePricingInExport);
    }
    if (typeof exportOptions.includeTemplateInExport === 'boolean') {
      setIncludeTemplateInExport(exportOptions.includeTemplateInExport);
    }
    if (typeof exportOptions.includeNijBegunInExport === 'boolean') {
      setIncludeNijBegunInExport(exportOptions.includeNijBegunInExport);
    } else {
      setIncludeNijBegunInExport(false);
    }
    if (typeof exportOptions.includeBonInExport === 'boolean') {
      setIncludeBonInExport(exportOptions.includeBonInExport);
    } else {
      setIncludeBonInExport(false);
    }
    if (typeof exportOptions.includeTotalPageInExport === 'boolean') {
      setIncludeTotalPageInExport(exportOptions.includeTotalPageInExport);
    } else if (typeof exportOptions.includeTemplateInExport === 'boolean') {
      // Template already includes the investment page
      setIncludeTotalPageInExport(exportOptions.includeTemplateInExport);
    } else {
      setIncludeTotalPageInExport(false);
    }
    const loadedClient = quote.quote_data.clientDetails || {};
    const qd = quote.quote_data || {};
    const signatureDataUrl = typeof qd.signature === 'string' ? qd.signature.trim() : '';
    let signedAgreement = null;
    if (signatureDataUrl || qd.signed_at || qd.signed_by_name) {
      let signedDate = '';
      if (qd.signed_at) {
        const parsed = new Date(qd.signed_at);
        signedDate = Number.isNaN(parsed.getTime())
          ? String(qd.signed_at)
          : parsed.toLocaleDateString('nl-NL', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            });
      }
      signedAgreement = {
        name: qd.signed_by_name || loadedClient.name || '',
        date: signedDate,
        signature: signatureDataUrl,
        subjectToFinance: Boolean(loadedClient.subjectToFinance),
      };
    }
    setActiveBackendQuote({
      id: quote.id,
      quoteNumber: quote.quote_number,
      clientDetails: {
        ...defaultClientDetails(),
        ...loadedClient,
        name: formatPersonName(loadedClient.name),
      },
      clientContactOverride: false,
      status: quote.status,
      totalAmount: quote.total_amount,
      isEditingSession: true,
      createdBy: quote.created_by ?? null,
      quoteCreator: quote.quote_data?.quoteCreator ?? null,
      creator_first_name: quote.creator_first_name || '',
      creator_last_name: quote.creator_last_name || '',
      creator_email: quote.creator_email || '',
      signedAgreement,
      created_at: quote.created_at || null,
      valid_until: quote.valid_until || null,
    });
  }, []);

  const clearBackendQuoteSession = useCallback(() => {
    setActiveBackendQuote(null);
  }, []);

  const startNewQuoteSession = useCallback(() => {
    setActiveBackendQuote(null);
    setSavedQuotes([]);
    setQuoteSections([]);
    setIncludeNijBegunInExport(false);
    setIncludeBonInExport(false);
  }, []);

  const patchActiveBackendQuote = useCallback((patch) => {
    setActiveBackendQuote((prev) => {
      if (prev) return { ...prev, ...patch };
      // After send-without-prior-save, bind the newly persisted My Quotes id
      if (patch?.id) {
        return {
          id: patch.id,
          quoteNumber: patch.quoteNumber || null,
          clientDetails: defaultClientDetails(),
          clientContactOverride: false,
          status: patch.status || 'sent',
          totalAmount: patch.totalAmount ?? null,
          isEditingSession: true,
          createdBy: patch.createdBy ?? null,
          quoteCreator: patch.quoteCreator ?? null,
          signedAgreement: null,
          ...patch,
        };
      }
      return prev;
    });
  }, []);

  const saveQuote = (quoteData) => {
    const clonedData = JSON.parse(JSON.stringify(quoteData));
    const newQuote = {
      id: Date.now().toString() + '-' + Math.random().toString(36).substr(2, 9),
      ...clonedData,
      savedAt: new Date().toISOString(),
    };
    setSavedQuotes((prev) => sortQuotesNewestFirst([newQuote, ...prev]));
    return newQuote.id;
  };

  const removeQuote = (id) => {
    setSavedQuotes(prev => prev.filter(quote => quote.id !== id));
  };

  const clearAllQuotes = () => {
    setSavedQuotes([]);
  };

  const clearQuoteSections = () => {
    setQuoteSections([]);
  };

  const saveQuoteSection = (name) => {
    if (!name?.trim() || savedQuotes.length === 0) return null;
    const section = createQuoteSection(name, savedQuotes);
    if (!section) return null;
    setQuoteSections((prev) => [...prev, section]);
    setSavedQuotes([]);
    return section.id;
  };

  /** Dissolve a section: move its drawings back into the open (ungrouped) list. */
  const ungroupQuoteSection = (id) => {
    const section = quoteSections.find((item) => item.id === id);
    if (!section) return;
    const drawings = JSON.parse(JSON.stringify(section.drawings || []));
    setQuoteSections((prev) => prev.filter((item) => item.id !== id));
    if (drawings.length > 0) {
      setSavedQuotes((prev) => sortQuotesNewestFirst([...drawings, ...prev]));
    }
  };

  /** Append current open drawings into an existing section, then clear the open list. */
  const addCurrentDrawingsToQuoteSection = (sectionId) => {
    if (!sectionId || savedQuotes.length === 0) return false;
    const exists = quoteSections.some((section) => section.id === sectionId);
    if (!exists) return false;
    const toAdd = JSON.parse(JSON.stringify(savedQuotes));
    setQuoteSections((prev) =>
      prev.map((section) =>
        section.id === sectionId
          ? { ...section, drawings: [...(section.drawings || []), ...toAdd] }
          : section
      )
    );
    setSavedQuotes([]);
    return true;
  };

  const removeQuoteSection = (id) => {
    setQuoteSections((prev) => prev.filter((section) => section.id !== id));
  };

  const updateQuote = (id, updates) => {
    setSavedQuotes((prev) =>
      prev.map((quote) => (quote.id === id ? { ...quote, ...updates } : quote))
    );
    // Keep sectioned drawings in sync (e.g. drawingName rename).
    setQuoteSections((prev) =>
      prev.map((section) => ({
        ...section,
        drawings: (section.drawings || []).map((drawing) =>
          drawing.id === id ? { ...drawing, ...updates } : drawing
        ),
      }))
    );
  };

  /** Map config on every drawing (current + sectioned) for export bulk options. */
  const updateAllQuoteDrawings = useCallback((mapDrawing) => {
    if (typeof mapDrawing !== 'function') return;
    setSavedQuotes((prev) => prev.map((drawing) => mapDrawing(drawing)));
    setQuoteSections((prev) =>
      prev.map((section) => ({
        ...section,
        drawings: (section.drawings || []).map((drawing) => mapDrawing(drawing)),
      })),
    );
  }, []);

  const setIncludeTemplateInExportWithTotalSync = useCallback((checked) => {
    const next = checked === true;
    setIncludeTemplateInExport(next);
    if (next) {
      setIncludeTotalPageInExport(true);
    }
  }, []);

  return (
    <QuotesContext.Provider
      value={{
        savedQuotes,
        quoteSections,
        saveQuote,
        saveQuoteSection,
        ungroupQuoteSection,
        addCurrentDrawingsToQuoteSection,
        removeQuote,
        removeQuoteSection,
        clearAllQuotes,
        clearQuoteSections,
        updateQuote,
        updateAllQuoteDrawings,
        includePricingInExport,
        setIncludePricingInExport,
        includeTemplateInExport,
        setIncludeTemplateInExport: setIncludeTemplateInExportWithTotalSync,
        includeNijBegunInExport,
        setIncludeNijBegunInExport,
        includeBonInExport,
        setIncludeBonInExport,
        includeTotalPageInExport,
        setIncludeTotalPageInExport,
        activeBackendQuote,
        loadBackendQuoteSession,
        clearBackendQuoteSession,
        startNewQuoteSession,
        patchActiveBackendQuote,
      }}
    >
      {children}
    </QuotesContext.Provider>
  );
};

